<template>
  <Grid
    class="financials-grid"
    :pageable="pageable"
    :data-items="transactions"
    :sort="sort"
    :skip="skip"
    :take="take"
    :page-size="take"
    :total="total"
    :columns="columns"
    :loader="loading"
    :filter="filter"
    :column-menu="!loading"
    :selected-field="selectedField"
    @filterchange="filterChangeHandler"
    @pagechange="pageChangeHandler"
    @selectionchange="changeSelection"
    @sortchange="sortChangeHandler"
    @rowclick="changeSelection"
    data-cy="financials-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="columns"
        :getItems="getPropValues"
        @sortchange="(e) => props.onSortchange(e)"
        @filterchange="(e) => props.onFilterchange(e)"
        @closemenu="(e: any) => props.onClosemenu(e)"
        @contentfocus="(e: any) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>
    <template v-slot:DetailsCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <input
          class="fa fa-external-link details-icon"
          type="image"
          alt="Detail Icon"
          @click.prevent="openDetails(dataItem)"
          :src="detailIconUrl"
        />
      </td>
    </template>
    <template v-slot:SelectCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <input
          v-if="selectionAvailable(dataItem)"
          type="checkbox"
          v-model="dataItem[field]"
          @change="emit('selectionChanged', [dataItem])"
        />
        <ProcessingIcon v-else-if="dataItem.PaymentPendingForThisDocumentNumber" />
      </td>
    </template>
    <template v-slot:TransactionTypeCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <button
          v-if="dataItem[field] === 'Invoice'"
          type="button"
          class="download-invoice-button"
          :disabled="downloadInProgress"
          @click="printInvoiceAsync(dataItem.DocumentNumber)"
          data-cy="download-invoice-button"
        >
          <Loader
            v-if="isDownloadingInvoice(dataItem.DocumentNumber)"
            class="download-invoice-loader"
            size="small"
            type="pulsing"
          />
          <font-awesome-icon v-else class="download-invoice-icon" icon="fa-solid fa-arrow-down" />
          {{ dataItem.IsMemoInvoice === true ? "Quote" : "Invoice" }}
        </button>
        <span v-else>{{ dataItem[field] }}</span>
      </td>
    </template>
    <template v-slot:BalanceFooter>
      <span>Total: {{ totalBalance ? CurrencyFormatter.format(totalBalance) : "$0.00" }}</span>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import {
  Grid,
  type GridColumnReorderEvent,
  type GridColumnProps,
  type GridFilterChangeEvent,
  type GridPageChangeEvent,
  type GridPagerSettings,
  type GridRowClickEvent,
  type GridSelectionChangeEvent,
} from "@progress/kendo-vue-grid";
import { Loader } from "@progress/kendo-vue-indicators";
import { computed, ref, watch, watchEffect } from "vue";
import type { CompositeFilterDescriptor, SortDescriptor } from "@progress/kendo-data-query";
import type { FinancialTransactionWithSelection } from "@/api/financials";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";

import { useMainStore } from "@/stores/useMainStore";
import detailIconUrl from "@/assets/img/DetailIcon.png";
import { CurrencyFormatter } from "@/shared/helpers/formatHelper";
import ProcessingIcon from "@/icons/ProcessingIcon.vue";
import type { ItemChangedOptions, ItemSortChangedOptions } from "@/Domain/ItemChangedOptions";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import {
  getFilterOperatorsForBoolean,
  getFilterOperatorsForDate,
  getFilterOperatorsForNumber,
} from "@/shared/helpers/filtersHelper";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { useNotificationStore } from "@/stores/useNotificationStore";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  transactions: FinancialTransactionWithSelection[];
  loading: boolean;
  skip: number;
  pageSize: number;
  total: number;
  filter: CompositeFilterDescriptor;
  allowSelection: boolean;
  sort: SortDescriptor[];
  getPropValues: (column: GridColumnPropsExtended) => Promise<FinancialTransactionWithSelection[]>;
}>();

const emit = defineEmits<{
  (e: "detailsClicked", transaction: FinancialTransactionWithSelection): void;
  (e: "invoicesChanged", options: ItemChangedOptions): void;
  (e: "selectionChanged", selectedInvoices: FinancialTransactionWithSelection[]): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "columnreorder", event: GridColumnReorderEvent): void;
  (e: "columnssubmit", columnsProps: GridColumnProps[]): void;
}>();

const skip = ref<number>(props.skip);
const take = ref<number>(props.pageSize);
const totalBalance = ref(main.balance);
const downloadingInvoiceId = ref<string | null>(null);

const selectedField = "selected";
const downloadInProgress = computed(() => downloadingInvoiceId.value != null);

const getColumns = function () {
  const cols = [] as GridColumnPropsExtended[];

  cols.push({
    cell: "DetailsCell",
    headerClassName: "hide-menu",
    sortable: false,
    filterable: false,
    width: "40px",
  });

  if (props.allowSelection) {
    cols.push({
      field: "selected",
      width: "50px",
      sortable: false,
      filterable: false,
      headerCell: "SelectHeaderCell",
      cell: "SelectCell",
      headerClassName: "hide-menu",
    });
  }

  cols.push(
    {
      title: "Transaction ID",
      field: "DocumentNumber",
      columnMenu: "columnMenu",
    },
    {
      title: "Date",
      field: "DocumentDate",
      columnMenu: "columnMenu",
      type: "date",
      format: "{0:MM/dd/yyyy}",
      allowedFilterOperators: getFilterOperatorsForDate(),
    },
    {
      title: "Invoice Type",
      field: "InvoiceType",
      columnMenu: "columnMenu",
    },
    {
      title: "Transaction Type",
      field: "DocumentType",
      columnMenu: "columnMenu",
      cell: "TransactionTypeCell",
    },
    {
      title: "Check # & Comment",
      field: "CheckOrComment",
      columnMenu: "columnMenu",
    },
    {
      title: "Debits",
      field: "Debits",
      columnMenu: "columnMenu",
      type: "number",
      format: "{0:c}",
      allowedFilterOperators: getFilterOperatorsForNumber(),
    },
    {
      title: "Credits",
      field: "Credits",
      columnMenu: "columnMenu",
      type: "number",
      format: "{0:c}",
      allowedFilterOperators: getFilterOperatorsForNumber(),
    },
    {
      title: "Bill For - Plant Name",
      field: "BillForPlantName",
      columnMenu: "columnMenu",
    },
    {
      title: "Bill For - Private Label",
      field: "BillForPrivateLabel",
      columnMenu: "columnMenu",
    }
  );
  cols.push({
    title: "Transaction Balance",
    field: "TransactionBalance",
    columnMenu: "columnMenu",
    type: "number",
    format: "{0:c}",
    footerCell: "BalanceFooter",
    allowedFilterOperators: getFilterOperatorsForNumber(),
  });
  return cols;
};

const columns = ref<GridColumnProps[]>(getColumns());

let pageSizes = [10, 20, 25, 50, 100];
if (!pageSizes.includes(take.value)) pageSizes.push(take.value);
pageSizes = pageSizes.sort((a, b) => a - b);
const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: pageSizes,
  previousNext: true,
});

const selectionAvailable = function (dataItem: FinancialTransactionWithSelection) {
  return (
    dataItem.TransactionBalance !== 0 &&
    dataItem.DocumentType === "Invoice" &&
    !dataItem.PaymentPendingForThisDocumentNumber &&
    props.allowSelection
  );
};

const filterChangeHandler = function (e: GridFilterChangeEvent) {
  skip.value = 0;
  emit("invoicesChanged", { skip: 0, take: take.value, filterEvent: e });
};

const sortChangeHandler = function (e: any) {
  const descriptors = e.sort as SortDescriptor[];
  if (!descriptors) return;
  skip.value = 0;
  emit("sortChanged", {
    field: descriptors[0].field,
    ascending: descriptors[0].dir === "asc",
  });
};

const pageChangeHandler = function (event: GridPageChangeEvent) {
  skip.value = event.page.skip;
  take.value = event.page.take;
  emit("invoicesChanged", {
    skip: event.page.skip,
    take: event.page.take,
    filterEvent: null,
  });
};

const isGridActionTarget = function (target: EventTarget | null) {
  return target instanceof Element && target.closest(".details-icon, .download-invoice-button") != null;
};

const changeSelection = function (event: GridSelectionChangeEvent | GridRowClickEvent) {
  // if clicked on the details icon or download link,
  // don't change the selection
  if (isGridActionTarget(event.event?.target)) return;

  const transaction = event.dataItem as FinancialTransactionWithSelection;
  if (!transaction || !selectionAvailable(transaction)) return;
  transaction.selected = !transaction.selected;
  emit("selectionChanged", [transaction]);
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  if (columnsProps) columns.value = columnsProps;
  console.debug("InvoicesGrid.vue, onColumnSubmit", columnsProps);
  emit("columnssubmit", columnsProps);
};

const openDetails = function (transaction: FinancialTransactionWithSelection) {
  emit("detailsClicked", transaction);
};

const isDownloadingInvoice = function (invoiceId: string) {
  return downloadingInvoiceId.value === invoiceId;
};

const onColumnReorder = function (e: GridColumnReorderEvent) {
  emit("columnreorder", e);
};

const printInvoiceAsync = async function (invoiceId: string) {
  if (downloadInProgress.value) return;

  downloadingInvoiceId.value = invoiceId;

  try {
    await handleResponsePromiseAsync(
      api.getDownload("PrintInvoice", {
        invoiceId: invoiceId,
      }),
      () => {},
      "FinancialsGrid.vue",
      "printInvoiceAsync",
      (error) => {
        notificationStore.notify({
          type: NotificationTypeEnum.error,
          message: `Error downloading invoice ${invoiceId}${error ? `. ${error}` : ""}`,
          duration: 5000,
        });
      }
    );
  } finally {
    downloadingInvoiceId.value = null;
  }
};

watchEffect(() => {
  skip.value = props.skip;
});
watchEffect(() => {
  totalBalance.value = main.balance;
});
watchEffect(() => {
  take.value = props.pageSize;
});

watch(
  () => props.allowSelection,
  () => {
    columns.value = getColumns();
  }
);

defineExpose({
  columns,
});
</script>

<style scoped lang="scss">
.financials-grid {
  height: 600px;
}

.k-grid {
  .download-invoice-button {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    height: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    text-align: left;
    color: #399CD3;

    svg {
      height: 12px;
    }

    &:disabled {
      cursor: default;
      opacity: 0.75;
    }
  }

  .k-pager {
    .k-input-inner {
      padding: 0.375rem 0.375rem;
    }
  }
}
</style>
